import { gql } from '@urql/core';
import { produce } from 'immer';

import { abTestsFeatureKeys } from './queries';
import { goalsFeatureKeys } from '/src/features/goals/queries';
import { defaultMutationFuction } from '../../graphql/defaultMutationFuction';
import { gettingStartedFeatureKeys } from '../getting-started/queries';

const AB_TEST_INFINITE_COLLECTION_QUERY_SPECS = [
  { queryKey: abTestsFeatureKeys.abTestList._def, responseField: 'abTestList' },
  { queryKey: abTestsFeatureKeys.abTestDashboardList._def, responseField: 'abTestDashboardList' },
];

function sameAbTestId(item, id) {
  const a = Number(item?.id);
  const b = Number(id);

  return Number.isFinite(a) && a > 0 && a === b;
}

function matchesAbTestId(id) {
  const numericId = Number(id);

  if (!Number.isFinite(numericId) || numericId <= 0) {
    return () => false;
  }

  return (query) => {
    const queryId = Number(query.queryKey.at(-1)?.id);
    return Number.isFinite(queryId) && queryId === numericId;
  };
}

function cancelAbTestCollectionQueries(queryClient) {
  return Promise.all([
    queryClient.cancelQueries({ queryKey: abTestsFeatureKeys.abTestList._def }),
    queryClient.cancelQueries({ queryKey: abTestsFeatureKeys.abTestDashboardList._def }),
    queryClient.cancelQueries({ queryKey: abTestsFeatureKeys.abTestHomeRecent._def }),
    queryClient.cancelQueries({ queryKey: abTestsFeatureKeys.abTestListPaginated._def }),
    queryClient.cancelQueries({ queryKey: abTestsFeatureKeys.abTestStatusCounts._def }),
  ]);
}

function cancelAbTestEntityQueries(queryClient, id) {
  const predicate = matchesAbTestId(id);

  return Promise.all([
    queryClient.cancelQueries({ queryKey: abTestsFeatureKeys.abTestDetail._def, predicate }),
    queryClient.cancelQueries({ queryKey: abTestsFeatureKeys.abTestEditorDetail._def, predicate }),
    queryClient.cancelQueries({ queryKey: abTestsFeatureKeys.abTestResultDetail._def, predicate }),
    queryClient.cancelQueries({ queryKey: abTestsFeatureKeys.abTestResultCounts._def, predicate }),
    queryClient.cancelQueries({ queryKey: abTestsFeatureKeys.abTestDistribution._def, predicate }),
  ]);
}

function updateAbTestStatusCounts(queryClient, updater) {
  queryClient.setQueriesData({ queryKey: abTestsFeatureKeys.abTestStatusCounts._def }, (old) =>
    old
      ? produce(old, (draft) => {
          if (!draft?.abTestStatusCounts) return;
          updater(draft.abTestStatusCounts);
        })
      : null,
  );
}

function updateAbTestCollectionQueries(queryClient, updater) {
  AB_TEST_INFINITE_COLLECTION_QUERY_SPECS.forEach(({ queryKey, responseField }) => {
    queryClient.setQueriesData({ queryKey }, (old) =>
      old
        ? produce(old, (draft) => {
            for (const page of draft.pages ?? []) {
              const list = page?.[responseField]?.list;
              if (Array.isArray(list)) {
                updater(list);
              }
            }
          })
        : null,
    );
  });

  queryClient.setQueriesData({ queryKey: abTestsFeatureKeys.abTestListPaginated._def }, (old) =>
    old
      ? produce(old, (draft) => {
          const list = draft?.abTestListPaginated?.list;
          if (Array.isArray(list)) {
            updater(list);
          }
        })
      : null,
  );

  queryClient.setQueriesData({ queryKey: abTestsFeatureKeys.abTestHomeRecent._def }, (old) =>
    old
      ? produce(old, (draft) => {
          if (Array.isArray(draft?.abTestHomeRecent)) {
            updater(draft.abTestHomeRecent);
          }
        })
      : null,
  );
}

function updateAbTestInCollections(queryClient, id, updater) {
  updateAbTestCollectionQueries(queryClient, (list) => {
    const abTest = list.find((item) => sameAbTestId(item, id));
    if (abTest) updater(abTest);
  });
}

function removeAbTestFromCollections(queryClient, id) {
  updateAbTestCollectionQueries(queryClient, (list) => {
    const index = list.findIndex((item) => sameAbTestId(item, id));
    if (index > -1) list.splice(index, 1);
  });
}

function updateAbTestEntities(queryClient, id, updater) {
  const predicate = matchesAbTestId(id);

  queryClient.setQueriesData({ queryKey: abTestsFeatureKeys.abTestDetail._def, predicate }, (old) =>
    old
      ? produce(old, (draft) => {
          const abTest = draft?.abTestDetail;
          if (sameAbTestId(abTest, id)) updater(abTest);
        })
      : null,
  );

  queryClient.setQueriesData({ queryKey: abTestsFeatureKeys.abTestEditorDetail._def, predicate }, (old) =>
    old
      ? produce(old, (draft) => {
          const abTest = draft?.abTestEditorDetail;
          if (sameAbTestId(abTest, id)) updater(abTest);
        })
      : null,
  );

  queryClient.setQueriesData({ queryKey: abTestsFeatureKeys.abTestResultDetail._def, predicate }, (old) =>
    old
      ? produce(old, (draft) => {
          const abTest = draft?.abTestResultDetail;
          if (sameAbTestId(abTest, id)) updater(abTest);
        })
      : null,
  );
}

function removeAbTestEntityQueries(queryClient, id) {
  const predicate = matchesAbTestId(id);

  queryClient.removeQueries({ queryKey: abTestsFeatureKeys.abTestDetail._def, predicate });
  queryClient.removeQueries({ queryKey: abTestsFeatureKeys.abTestEditorDetail._def, predicate });
  queryClient.removeQueries({ queryKey: abTestsFeatureKeys.abTestResultDetail._def, predicate });
  queryClient.removeQueries({ queryKey: abTestsFeatureKeys.abTestResultCounts._def, predicate });
  queryClient.removeQueries({ queryKey: abTestsFeatureKeys.abTestDistribution._def, predicate });
}

function setAbTestName(queryClient, id, name) {
  updateAbTestInCollections(queryClient, id, (abTest) => {
    abTest.name = name;
  });

  updateAbTestEntities(queryClient, id, (abTest) => {
    abTest.name = name;
  });
}

function setAbTestVariantsCount(queryClient, id, variantsCount) {
  if (!Number.isFinite(Number(variantsCount))) return;

  updateAbTestInCollections(queryClient, id, (abTest) => {
    abTest.variantsCount = Number(variantsCount);
  });

  updateAbTestEntities(queryClient, id, (abTest) => {
    abTest.variantsCount = Number(variantsCount);
  });
}

function invalidateAbTestCollectionQueries(queryClient, options = {}) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: abTestsFeatureKeys.abTestList._def, ...options }),
    queryClient.invalidateQueries({ queryKey: abTestsFeatureKeys.abTestDashboardList._def, ...options }),
    queryClient.invalidateQueries({ queryKey: abTestsFeatureKeys.abTestHomeRecent._def, ...options }),
    queryClient.invalidateQueries({ queryKey: abTestsFeatureKeys.abTestListPaginated._def, ...options }),
    queryClient.invalidateQueries({ queryKey: abTestsFeatureKeys.abTestStatusCounts._def, ...options }),
  ]);
}

function refetchAbTestCollectionQueries(queryClient) {
  return Promise.all([
    queryClient.refetchQueries({ queryKey: abTestsFeatureKeys.abTestList._def, type: 'all' }),
    queryClient.refetchQueries({ queryKey: abTestsFeatureKeys.abTestDashboardList._def, type: 'all' }),
    queryClient.refetchQueries({ queryKey: abTestsFeatureKeys.abTestHomeRecent._def, type: 'all' }),
    queryClient.refetchQueries({ queryKey: abTestsFeatureKeys.abTestListPaginated._def, type: 'all' }),
    queryClient.refetchQueries({ queryKey: abTestsFeatureKeys.abTestStatusCounts._def, type: 'all' }),
  ]);
}

function refreshAbTestCollectionQueries(queryClient) {
  return Promise.all([
    invalidateAbTestCollectionQueries(queryClient, { refetchType: 'none' }),
    refetchAbTestCollectionQueries(queryClient),
  ]);
}

function invalidateAbTestStaticQueries(queryClient, id, options = {}) {
  const predicate = matchesAbTestId(id);

  return Promise.all([
    queryClient.invalidateQueries({ queryKey: abTestsFeatureKeys.abTestDetail._def, predicate, ...options }),
    queryClient.invalidateQueries({ queryKey: abTestsFeatureKeys.abTestEditorDetail._def, predicate, ...options }),
    queryClient.invalidateQueries({ queryKey: abTestsFeatureKeys.abTestResultDetail._def, predicate, ...options }),
  ]);
}

function invalidateAbTestResultMetricQueries(queryClient, id, options = {}) {
  const predicate = matchesAbTestId(id);

  return Promise.all([
    queryClient.invalidateQueries({ queryKey: abTestsFeatureKeys.abTestResultCounts._def, predicate, ...options }),
    queryClient.invalidateQueries({ queryKey: abTestsFeatureKeys.abTestDistribution._def, predicate, ...options }),
  ]);
}

function invalidateAbTestEntityQueries(queryClient, id, options = {}) {
  return Promise.all([
    invalidateAbTestStaticQueries(queryClient, id, options),
    invalidateAbTestResultMetricQueries(queryClient, id, options),
  ]);
}

function applyStoppedPermissions(permissions = null) {
  if (!permissions) return permissions;

  return {
    ...permissions,
    canAddVariants: false,
    canAdjustWeight: false,
    canChangeGoal: false,
    canStop: false,
  };
}

function applyStoppedState(abTest, stoppedAtDate) {
  if (!abTest) return;

  abTest.stoppedAt = stoppedAtDate;
  abTest.status = 'STOPPED';

  if (abTest.permissions) {
    abTest.permissions = applyStoppedPermissions(abTest.permissions);
  }
}

function invalidateGoalQueriesWithAbTestCounts(queryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: goalsFeatureKeys.goalListSummary._def }),
    queryClient.invalidateQueries({ queryKey: goalsFeatureKeys.goalCardList._def }),
    queryClient.invalidateQueries({ queryKey: goalsFeatureKeys.goalDetail._def }),
  ]);
}

export const abTestCreateMutation = ({ client, queryClient }) => ({
  mutationFn: ({ args }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestCreate($args: AbTestCreateInput!) {
          abTestCreate(args: $args) {
            id
          }
        }
      `,
      {
        args,
      },
    ),
  onSuccess: () => {
    return Promise.all([
      refreshAbTestCollectionQueries(queryClient),
      queryClient.invalidateQueries({ ...gettingStartedFeatureKeys.gettingStarted }),
      invalidateGoalQueriesWithAbTestCounts(queryClient),
      queryClient.invalidateQueries({
        queryKey: ['sites'],
      }),
    ]);
  },
});

export const abTestUpdateMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id, args }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestUpdate($id: Int!, $args: AbTestCreateInput!) {
          abTestUpdate(id: $id, args: $args) {
            id
          }
        }
      `,
      { id, args },
    ),
  onSuccess: (data) => {
    return Promise.all([
      refreshAbTestCollectionQueries(queryClient),
      invalidateAbTestStaticQueries(queryClient, data.abTestUpdate.id),
    ]);
  },
});

export const abTestDeleteMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestDelete($id: Int!) {
          abTestDelete(id: $id) {
            id
            status
          }
        }
      `,
      { id },
    ),
  onSuccess: (data) => {
    removeAbTestFromCollections(queryClient, data.abTestDelete.id);
    updateAbTestStatusCounts(queryClient, (counts) => {
      counts.total = Math.max(0, (counts.total ?? 0) - 1);

      const statusKey = data?.abTestDelete?.status?.toLowerCase();

      if (statusKey && Object.hasOwn(counts, statusKey)) {
        counts[statusKey] = Math.max(0, (counts[statusKey] ?? 0) - 1);
      }
    });

    return Promise.all([
      refreshAbTestCollectionQueries(queryClient),
      invalidateAbTestEntityQueries(queryClient, data.abTestDelete.id, { refetchType: 'none' }),
      removeAbTestEntityQueries(queryClient, data.abTestDelete.id),
      invalidateGoalQueriesWithAbTestCounts(queryClient),
    ]);
  },
});

export const abTestStopMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestStop($id: Int!) {
          abTestStop(id: $id) {
            id
          }
        }
      `,
      { id },
    ),
  onMutate: async (variables) => {
    const stoppedAtDate = Math.floor(Date.now() / 1000);
    const predicate = matchesAbTestId(variables.id);

    await Promise.all([
      cancelAbTestCollectionQueries(queryClient),
      cancelAbTestEntityQueries(queryClient, variables.id),
    ]);

    // Snapshot prior state for rollback
    const previousDetail = queryClient.getQueriesData({
      queryKey: abTestsFeatureKeys.abTestDetail._def,
      predicate,
    });
    const previousEditorDetail = queryClient.getQueriesData({
      queryKey: abTestsFeatureKeys.abTestEditorDetail._def,
      predicate,
    });
    const previousResultDetail = queryClient.getQueriesData({
      queryKey: abTestsFeatureKeys.abTestResultDetail._def,
      predicate,
    });
    const previousCollections = [
      ...AB_TEST_INFINITE_COLLECTION_QUERY_SPECS.map(({ queryKey }) => queryClient.getQueriesData({ queryKey })),
      queryClient.getQueriesData({ queryKey: abTestsFeatureKeys.abTestListPaginated._def }),
      queryClient.getQueriesData({ queryKey: abTestsFeatureKeys.abTestHomeRecent._def }),
    ];
    const previousStatusCounts = queryClient.getQueriesData({
      queryKey: abTestsFeatureKeys.abTestStatusCounts._def,
    });

    // Apply optimistic updates
    queryClient.setQueriesData({ queryKey: abTestsFeatureKeys.abTestDetail._def, predicate }, (old) =>
      old
        ? produce(old, (draft) => {
            applyStoppedState(draft.abTestDetail, stoppedAtDate);
          })
        : null,
    );

    queryClient.setQueriesData({ queryKey: abTestsFeatureKeys.abTestEditorDetail._def, predicate }, (old) =>
      old
        ? produce(old, (draft) => {
            applyStoppedState(draft.abTestEditorDetail, stoppedAtDate);
          })
        : null,
    );

    queryClient.setQueriesData({ queryKey: abTestsFeatureKeys.abTestResultDetail._def, predicate }, (old) =>
      old
        ? produce(old, (draft) => {
            applyStoppedState(draft.abTestResultDetail, stoppedAtDate);
          })
        : null,
    );

    updateAbTestInCollections(queryClient, variables.id, (abTest) => {
      applyStoppedState(abTest, stoppedAtDate);
    });

    updateAbTestStatusCounts(queryClient, (counts) => {
      counts.running = Math.max(0, (counts.running ?? 0) - 1);
      counts.stopped = (counts.stopped ?? 0) + 1;
    });

    return {
      previousDetail,
      previousEditorDetail,
      previousResultDetail,
      previousCollections,
      previousStatusCounts,
    };
  },
  onError: (_error, _variables, context) => {
    if (!context) return;

    // Restore all cached snapshots
    for (const [key, data] of context.previousDetail) {
      queryClient.setQueryData(key, data);
    }
    for (const [key, data] of context.previousEditorDetail) {
      queryClient.setQueryData(key, data);
    }
    for (const [key, data] of context.previousResultDetail) {
      queryClient.setQueryData(key, data);
    }
    for (const entries of context.previousCollections) {
      for (const [key, data] of entries) {
        queryClient.setQueryData(key, data);
      }
    }
    for (const [key, data] of context.previousStatusCounts) {
      queryClient.setQueryData(key, data);
    }
  },
  onSuccess: () => {
    return invalidateGoalQueriesWithAbTestCounts(queryClient);
  },
  onSettled: (_, __, variables) => {
    return Promise.all([
      refreshAbTestCollectionQueries(queryClient),
      invalidateAbTestEntityQueries(queryClient, variables.id),
    ]);
  },
});

export const abTestPublishMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestPublish($id: Int!) {
          abTestPublish(id: $id) {
            id
          }
        }
      `,
      { id },
    ),
  onSuccess: (data) => {
    return Promise.all([
      refreshAbTestCollectionQueries(queryClient),
      invalidateAbTestStaticQueries(queryClient, data.abTestPublish.id),
      invalidateGoalQueriesWithAbTestCounts(queryClient),
    ]);
  },
});

export const abTestChangeGoalMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id, goalId }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestChangeGoal($goalId: Int!, $id: Int!) {
          abTestChangeGoal(goalId: $goalId, id: $id) {
            id
          }
        }
      `,
      { id, goalId },
    ),
  onSuccess: (data) => {
    return Promise.all([
      refreshAbTestCollectionQueries(queryClient),
      invalidateAbTestEntityQueries(queryClient, data.abTestChangeGoal.id),
      invalidateGoalQueriesWithAbTestCounts(queryClient),
    ]);
  },
});

export const abTestAddVariantsMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id, variants }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestAddVariants($id: Int!, $variants: [AbTestVariantInput!]!) {
          abTestAddVariants(id: $id, variants: $variants) {
            id
          }
        }
      `,
      { id, variants },
    ),
  onSuccess: (data, variables) => {
    const variantsCount = Number(variables?.variants?.length);

    if (Number.isFinite(variantsCount)) {
      updateAbTestInCollections(queryClient, data.abTestAddVariants.id, (abTest) => {
        abTest.variantsCount = (abTest.variantsCount ?? 0) + variantsCount;
      });
    }

    return Promise.all([
      refreshAbTestCollectionQueries(queryClient),
      invalidateAbTestEntityQueries(queryClient, data.abTestAddVariants.id),
    ]);
  },
});

export const abTestUpdateVariantsMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id, variants }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestUpdateVariants($id: Int!, $variants: [AbTestVariantInput!]!) {
          abTestUpdateVariants(id: $id, variants: $variants) {
            id
          }
        }
      `,
      { id, variants },
    ),
  onSuccess: (data) => {
    return Promise.all([
      refreshAbTestCollectionQueries(queryClient),
      invalidateAbTestEntityQueries(queryClient, data.abTestUpdateVariants.id),
    ]);
  },
});

export const abTestRetireVariantMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id, variantId }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestRetireVariant($id: Int!, $variantId: Int!) {
          abTestRetireVariant(id: $id, variantId: $variantId) {
            id
          }
        }
      `,
      { id, variantId },
    ),
  onSuccess: (data) => {
    return Promise.all([
      refreshAbTestCollectionQueries(queryClient),
      invalidateAbTestEntityQueries(queryClient, data.abTestRetireVariant.id),
    ]);
  },
});

export const abTestDeleteVariantMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id, variantId }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestDeleteVariant($id: Int!, $variantId: Int!) {
          abTestDeleteVariant(id: $id, variantId: $variantId) {
            id
          }
        }
      `,
      { id, variantId },
    ),
  onSuccess: (data) => {
    updateAbTestInCollections(queryClient, data.abTestDeleteVariant.id, (abTest) => {
      abTest.variantsCount = Math.max(0, (abTest.variantsCount ?? 0) - 1);
    });

    return Promise.all([
      refreshAbTestCollectionQueries(queryClient),
      invalidateAbTestEntityQueries(queryClient, data.abTestDeleteVariant.id),
    ]);
  },
});

export const abTestCheckPageMutation = ({ client }) => ({
  mutationFn: ({ checkType, url }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestCheckPage($checkType: AbTestCheckPageType!, $url: String!) {
          abTestCheckPage(checkType: $checkType, url: $url) {
            domain
            errors
            needToUpdateSiteId
            siteCreationSuccessful
            siteId
          }
        }
      `,
      { checkType, url },
    ),
});

export const abTestSetTrafficSplitMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id, autoReweight, variants = [] }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestSetTrafficSplit($id: Int!, $autoReweight: Boolean!, $variants: [AbTestVariantWeightInput!]!) {
          abTestSetTrafficSplit(id: $id, autoReweight: $autoReweight, variants: $variants) {
            id
          }
        }
      `,
      { id, autoReweight, variants },
    ),
  onSuccess: (data) => {
    return Promise.all([
      refreshAbTestCollectionQueries(queryClient),
      invalidateAbTestEntityQueries(queryClient, data.abTestSetTrafficSplit.id),
    ]);
  },
});

export const abTestRenameMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id, name }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestRename($name: String!, $id: Int!) {
          abTestRename(name: $name, id: $id) {
            id
            name
          }
        }
      `,
      { id, name },
    ),
  onSuccess: (data) => {
    setAbTestName(queryClient, data.abTestRename.id, data.abTestRename.name);

    return Promise.all([
      refreshAbTestCollectionQueries(queryClient),
      invalidateAbTestStaticQueries(queryClient, data.abTestRename.id),
    ]);
  },
});

export const abTestUpdateDraftMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id, args }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestUpdateDraft($id: Int!, $args: AbTestCreateInput!) {
          abTestUpdateDraft(id: $id, args: $args) {
            id
          }
        }
      `,
      { id, args },
    ),
  onSuccess: (data, variables) => {
    setAbTestVariantsCount(queryClient, data.abTestUpdateDraft.id, variables?.args?.variants?.length);

    return Promise.all([
      refreshAbTestCollectionQueries(queryClient),
      invalidateAbTestStaticQueries(queryClient, data.abTestUpdateDraft.id),
    ]);
  },
});

export const abTestDuplicateMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestDuplicate($id: Int!) {
          abTestDuplicate(id: $id) {
            id
          }
        }
      `,
      { id },
    ),
  onSuccess: () => {
    return refreshAbTestCollectionQueries(queryClient);
  },
});

export const abTestUpdateVariantDescriptionMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id, variantId, description }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestUpdateVariantDescription($id: Int!, $variantId: Int!, $description: String!) {
          abTestUpdateVariantDescription(id: $id, variantId: $variantId, description: $description) {
            id
          }
        }
      `,
      { id, variantId, description },
    ),
  onSuccess: (data) => {
    return invalidateAbTestStaticQueries(queryClient, data.abTestUpdateVariantDescription.id);
  },
});

export const abTestUpdateNotesMutation = ({ client, queryClient }) => ({
  mutationFn: ({ id, notes }) =>
    defaultMutationFuction(
      client,
      gql`
        mutation AbTestUpdateNotes($id: Int!, $notes: String!) {
          abTestUpdateNotes(id: $id, notes: $notes) {
            id
          }
        }
      `,
      { id, notes },
    ),
  onSuccess: (data) => {
    return invalidateAbTestStaticQueries(queryClient, data.abTestUpdateNotes.id);
  },
});
